import { Alert, Box, Button, Center, Loader, LoadingOverlay, Text } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { ReactNode, useEffect, useRef } from 'react';
import { PermissionError } from './PermissionError';

export interface DataContainerProps {
	loading?: boolean;
	error?: unknown;
	permissionErrors?: Array<{
		message: string;
		code?: string | null;
		details?: string | null;
	}> | null;
	children: ReactNode;
	loadingComponent?: ReactNode;
	errorComponent?: ReactNode;
	emptyState?: ReactNode;
	isEmpty?: boolean;
}

export function DataContainer({
	loading,
	error,
	permissionErrors,
	children,
	loadingComponent,
	errorComponent,
	emptyState,
	isEmpty,
}: DataContainerProps) {
	// Track if we've ever shown content (not loading) before
	const hasShownContentRef = useRef(false);

	// Once we've shown content, remember it
	useEffect(() => {
		if (!loading && !error) {
			hasShownContentRef.current = true;
		}
	}, [loading, error]);
	// Check for permission errors first
	if (permissionErrors && permissionErrors.length > 0) {
		const permissionError = permissionErrors[0];
		const isPermissionDenied =
			permissionError.code === '403 Forbidden' ||
			permissionError.message.toLowerCase().includes('permission');

		if (isPermissionDenied) {
			return (
				<PermissionError
					message={permissionError.message}
					details={permissionError.details || undefined}
				/>
			);
		}
	}

	// Loading state - show full loader only for the very first load
	// After content has been shown once, always use overlay
	const shouldShowOverlay = loading && hasShownContentRef.current;
	const shouldShowFullLoader = loading && !hasShownContentRef.current;

	if (shouldShowFullLoader) {
		return (
			loadingComponent || (
				<Center h="100%">
					<Loader />
				</Center>
			)
		);
	}

	// General error state
	if (error) {
		return (
			errorComponent || (
				<Center h="100%" style={{ flexDirection: 'column', gap: 16 }}>
					<Alert
						style={{ minWidth: '350px' }}
						icon={<IconAlertCircle size={16} />}
						title="Error"
						color="red"
						variant="light"
					>
						<Text size="sm">
							{error &&
							typeof error === 'object' &&
							'message' in error &&
							typeof error.message === 'string'
								? error.message
								: 'An unexpected error occurred'}
						</Text>
					</Alert>
					<Button
						variant="filled"
						size="sm"
						leftSection={<IconRefresh size={16} />}
						onClick={() => window.location.reload()}
					>
						Refresh
					</Button>
				</Center>
			)
		);
	}

	// Empty state
	if (isEmpty && emptyState) {
		return <>{emptyState}</>;
	}

	// Render children with optional loading overlay
	if (shouldShowOverlay) {
		return (
			<Box pos="relative">
				<LoadingOverlay
					visible={true}
					overlayProps={{ radius: 'sm', blur: 2 }}
					loaderProps={{ size: 'md' }}
				/>
				{children}
			</Box>
		);
	}

	// Render children normally
	return <>{children}</>;
}
